import type { ReactElement, SyntheticEvent } from "react"
import type { UnderlineLinkAtmProps } from "@onestore/hel/dist/components/atoms/UnderlineLinkAtm"
import UnderlineLinkAtm from "@onestore/hel/dist/components/atoms/UnderlineLinkAtm"
import SimplePageHeaderMol from "@onestore/hel/dist/components/molecules/SimplePageHeaderMol"
import StepsMol from "@onestore/hel/dist/components/molecules/StepsMol"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import _t from "@core/i18n"
import useMenuQuery from "@gatsby-plugin-onestore/hooks/useMenuQuery"
import TogglePriceType from "~/components/TogglePriceType"
import { stepsIconColor } from "~/configs/checkoutConfig"
import { sendBackToShopEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import { getBackToShopUrl } from "~/store/bonus/selectors"
import { useAppSelector } from "~/store/hooks"
import { getStepsData } from "./helpers/getStepsData"

export type HeaderWithStepsProps = {
  isBasketPage?: boolean
  isBonusPage?: boolean
  isCheckoutPage?: boolean
  hasPriceTypeSwitcher?: boolean
  linkData?: Pick<UnderlineLinkAtmProps, "to" | "title" | "onClick">
}

function HeaderWithSteps({
  isBonusPage,
  isBasketPage,
  isCheckoutPage,
  linkData,
  hasPriceTypeSwitcher = false,
}: HeaderWithStepsProps): ReactElement | null {
  const { menu } = useMenuQuery()
  const backToShopUrl = useAppSelector(getBackToShopUrl)

  const stepsData = getStepsData(isBonusPage, isBasketPage, isCheckoutPage)
  const slicedStepsData = stepsData?.slice(1)

  const linkElement = (
    <UnderlineLinkAtm
      to={!isEmpty(linkData) ? linkData.to : backToShopUrl}
      title={
        !isEmpty(linkData) ? linkData.title : _t("checkout.backToMainPage")
      }
      typography="tiny1"
      onClick={(event: SyntheticEvent) => {
        if (!isEmpty(linkData) && linkData.onClick) {
          linkData.onClick(event)
        } else {
          sendBackToShopEvent()
        }
      }}
    >
      {!isEmpty(linkData) ? linkData.title : _t("checkout.backToMainPage")}
    </UnderlineLinkAtm>
  )

  const stepsElement = !isEmpty(slicedStepsData) ? (
    <StepsMol steps={slicedStepsData} iconColor={stepsIconColor} />
  ) : null

  const priceTypeSwitcher = hasPriceTypeSwitcher ? (
    <AlignContentOrg horizontalAlign="right" horizontalAlignType="both">
      <TogglePriceType />
    </AlignContentOrg>
  ) : null

  return (
    <SimplePageHeaderMol
      logo={{
        title: menu.flatData.siteName,
        to: backToShopUrl,
        src: menu.flatData.logo[0].url,
        alt: `Logo ${menu.flatData.siteName}`,
      }}
      mobileBottomChildren={stepsElement}
    >
      {!isEmpty(stepsElement) ? (
        <BreakpointVisibilityOrg from="medium">
          {stepsElement}
        </BreakpointVisibilityOrg>
      ) : null}

      <FlexContainerOrg alignItems="center" gapSpace={2}>
        {priceTypeSwitcher}

        {linkElement}
      </FlexContainerOrg>
    </SimplePageHeaderMol>
  )
}

export default HeaderWithSteps
